import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { useConfirm } from '../components/common/ConfirmDialog';

// Import API_BASE_URL and isCustomerSubdomain from constants
import {
  API_BASE_URL,
  isCustomerSubdomain,
} from '../utils/constants';

export const useApi = () => {
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [groups, setGroups] = useState([]);
  const [groupsTotal, setGroupsTotal] = useState(0);
  const [groupMembers, setGroupMembers] = useState([]);
  const [permissionSets, setPermissionSets] = useState([]);
  const [awsManagedPolicies, setAwsManagedPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Confirmation dialog hook
  const confirm = useConfirm();

  // Fix 1: Add reference to track pending requests
  const pendingRequests = useRef(0);

  // Cache control for AWS policies
  const policiesCacheRef = useRef({
    data: [],
    timestamp: 0,
    isFetching: false
  });
  
  // Cache expiration time (15 minutes)
  const CACHE_EXPIRATION = 15 * 60 * 1000;

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Fix 2: Improved API call handler that tracks number of pending requests
  const handleApiCall = async (apiCall, successMessage = null) => {
    pendingRequests.current++; // Increment pending requests counter
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiCall();
      if (successMessage) {
        setSuccess(successMessage);
      }
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      pendingRequests.current--; // Decrement pending requests counter
      // Only set loading to false when all requests are completed
      if (pendingRequests.current === 0) {
        setLoading(false);
      }
    }
  };

  const fetchAllUsers = async () => {
    const result = await handleApiCall(() => apiService.getAllUsers({ page: 1, pageSize: 1000 }));
    if (result && result.success) {
      setUsers(result.data.users || []);
      // The array is one page, so its length is not the realm size. Anything
      // reporting a count needs the server's total or it under-reports large realms.
      setUsersTotal(
        typeof result.data.total === 'number' ? result.data.total : (result.data.users || []).length
      );
    }
  };

  // Add fetchAllGroups function if it's missing
  const fetchAllGroups = async () => {
    try {
      setLoading(true);
      const result = await apiService.getAllGroups({ page: 1, pageSize: 1000 });

      if (result && result.success) {
        const groupsData = result.data.groups || [];
        setGroups(groupsData);
        setGroupsTotal(typeof result.data.total === 'number' ? result.data.total : groupsData.length);
        return groupsData;
      } else {
        // Handle case where API might return groups directly
        if (result && Array.isArray(result)) {
          setGroups(result);
          return result;
        }
        setGroups([]);
        return [];
      }
    } catch (err) {
      setError(`Failed to fetch groups: ${err.message}`);
      setGroups([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Enhanced fetchGroupMembers with debouncing
  const fetchGroupMembers = useCallback(async (groupName) => {
    // Simple debouncing - don't allow same group fetch within 1 second
    const now = Date.now();
    const lastFetch = fetchGroupMembers.lastFetch || {};
    
    if (lastFetch[groupName] && now - lastFetch[groupName] < 1000) {
      return groupMembers; // Return existing data if called too soon
    }
    
    fetchGroupMembers.lastFetch = { ...lastFetch, [groupName]: now };
    
    const result = await handleApiCall(
      () => apiService.getGroupMembers(groupName),
      null
    );
    
    if (result && result.success) {
      const members = result.data.members || [];
      setGroupMembers(members);
      return members;
    } else {
      setGroupMembers([]);
      return [];
    }
  }, [groupMembers]); // Remove from dependency array to prevent loops

  const createUser = async (userData) => {
    const result = await handleApiCall(
      () => apiService.createUser(userData),
      'User created successfully'
    );
    if (result && result.success) {
      fetchAllUsers();
      return true;
    }
    return false;
  };

  const createGroup = async (groupData) => {
    const result = await handleApiCall(
      () => apiService.createGroup(groupData),
      'Group created successfully'
    );
    if (result && result.success) {
      fetchAllGroups();
      return true;
    }
    return false;
  };

  const deleteGroup = async (groupName) => {
    const confirmed = await confirm({
      title: 'Delete Group',
      message: `Are you sure you want to delete group: ${groupName}?`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) {
      return false;
    }

    try {
      setLoading(true);
      const result = await apiService.deleteGroup(groupName);

      // Handle both success response formats
      if (result && (result.success !== false)) {
        toast.success('Group deleted successfully');
        await fetchAllGroups();
        return true;
      }
      // If result exists but not successful, show the error message
      if (result && result.message) {
        toast.error(result.message);
      }
      return false;
    } catch (err) {
      toast.error(err.message || 'Failed to delete group');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getUser = async (username) => {
    const result = await handleApiCall(() => apiService.getUser(username));
    if (result && result.success) {
      return result.data;
    }
    return null;
  };

  const getGroup = async (groupName) => {
    const result = await handleApiCall(() => apiService.getGroup(groupName));
    if (result && result.success) {
      return result.data;
    }
    return null;
  };

  const updateUser = async (username, userData) => {
    const result = await handleApiCall(
      () => apiService.updateUser(username, userData),
      'User updated successfully'
    );
    if (result && result.success) {
      fetchAllUsers();
      return true;
    }
    return false;
  };

  const deleteUser = async (username) => {
    const confirmed = await confirm({
      title: 'Delete User',
      message: `Are you sure you want to delete user: ${username}?`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) {
      return false;
    }

    try {
      setLoading(true);
      const result = await apiService.deleteUser(username);

      if (result && result.success) {
        toast.success('User deleted successfully');
        await fetchAllUsers();
        return true;
      }
      // If result exists but not successful, show the error message
      if (result && result.message) {
        toast.error(result.message);
      }
      return false;
    } catch (err) {
      toast.error(err.message || 'Failed to delete user');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Enhanced assignUsersToGroup with better debugging
  const assignUsersToGroup = async (groupName, usernames) => {
    if (!usernames || usernames.length === 0) return false;
    
    const result = await handleApiCall(
      () => apiService.assignUsersToGroup(groupName, usernames),
      `${usernames.length} user(s) assigned to ${groupName} successfully`
    );
    
    if (result && result.success) {
      await fetchGroupMembers(groupName);
      return true;
    }
    return false;
  };
  
  // Remove users from group
  const removeUsersFromGroup = async (groupName, users) => {
    const result = await handleApiCall(
      () => apiService.removeUsersFromGroup(groupName, users),
      `Users removed from ${groupName} group successfully`
    );
    if (result && result.success) {
      await fetchGroupMembers(groupName);
      return true;
    }
    return false;
  };

  // Permission Set operations
  const fetchAllPermissionSets = async () => {
    const result = await handleApiCall(() => apiService.getAllPermissionSets({ page: 1, pageSize: 1000 }));
    if (result && result.success) {
      setPermissionSets(result.data.permissionSets || []);
      return result.data.permissionSets || [];
    }
    return [];
  };

  const getPermissionSet = async (id) => {
    const result = await handleApiCall(() => apiService.getPermissionSet(id));
    if (result && result.success) {
      return result.data;
    }
    return null;
  };

  const createPermissionSet = async (permissionSetData) => {
    const result = await handleApiCall(
      () => apiService.createPermissionSet(permissionSetData),
      'Permission set created successfully'
    );
    if (result && result.success) {
      fetchAllPermissionSets();
      return true;
    }
    return false;
  };

  const updatePermissionSet = async (id, permissionSetData) => {
    const result = await handleApiCall(
      () => apiService.updatePermissionSet(id, permissionSetData),
      'Permission set updated successfully - roles will be synchronized across accounts'
    );
    if (result && result.success) {
      fetchAllPermissionSets();
      return true;
    }
    return false;
  };

  const deletePermissionSet = async (id, name) => {
    const result = await handleApiCall(
      () => apiService.deletePermissionSet(id),
      'Permission set deleted successfully'
    );
    if (result && result.success) {
      fetchAllPermissionSets();
      return true;
    }
    return false;
  };

  // AWS Managed Policy operations with caching
  const fetchAwsManagedPolicies = useCallback(async (onlyAttachable = true, forceRefresh = false) => {
    // Check if we have cached data that's not expired
    const now = Date.now();
    const cache = policiesCacheRef.current;
    
    // If there's an ongoing fetch, return the promise for it rather than starting a new request
    if (cache.isFetching) {
      return new Promise(resolve => {
        // Check every 100ms if fetch has completed
        const checkInterval = setInterval(() => {
          if (!cache.isFetching) {
            clearInterval(checkInterval);
            resolve(cache.data);
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve(cache.data || []);
        }, 10000);
      });
    }
    
    // Use cache if it's valid and we're not forcing a refresh
    if (
      !forceRefresh && 
      cache.data.length > 0 && 
      now - cache.timestamp < CACHE_EXPIRATION
    ) {
      setAwsManagedPolicies(cache.data);
      return Promise.resolve(cache.data);
    }

    // Set fetching flag
    cache.isFetching = true;
    
    try {
      const result = await handleApiCall(() => apiService.getAwsManagedPolicies(onlyAttachable));
      
      let policies = [];
      
      if (result && result.success) {
        policies = result.data?.policies || [];
      } else if (result && Array.isArray(result.policies)) {
        // Handle direct policies array in result
        policies = result.policies;
      } else if (Array.isArray(result)) {
        // Handle case where result is directly an array
        policies = result;
      }
      
      // Update cache
      policiesCacheRef.current = {
        data: policies,
        timestamp: now,
        isFetching: false
      };
      
      // Update state
      setAwsManagedPolicies(policies);
      return policies;
    } catch (error) {
      console.error('Error fetching AWS managed policies:', error);
      // Set empty array on error but still cache it
      policiesCacheRef.current = {
        data: [],
        timestamp: now,
        isFetching: false
      };
      
      setAwsManagedPolicies([]);
      return [];
    } finally {
      cache.isFetching = false;
    }
  }, []);  // Empty dependency array as this function manages its own state

  const getAwsManagedPolicy = async (arn) => {
    // First check if we have this policy in our cache
    const cachedPolicies = policiesCacheRef.current.data;
    const cachedPolicy = cachedPolicies.find(p => p.arn === arn);
    
    if (cachedPolicy) {
      return { 
        success: true,
        data: cachedPolicy
      };
    }
    
    // If not in cache, fetch from API
    return handleApiCall(() => apiService.getAwsManagedPolicy(arn));
  };

  // New state variables
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [permissionSetAssignments, setPermissionSetAssignments] = useState([]);

  // AWS Account operations
  const fetchAwsAccounts = async () => {
    // Use the database endpoint instead of the env-based endpoint
    const result = await handleApiCall(() => apiService.getAwsAccountsFromDB());
    if (result && result.success) {
      const accounts = result.data.accounts || [];
      setAwsAccounts(accounts);
      return accounts;
    } else {
      // Handle case where API might return accounts directly
      if (result && Array.isArray(result)) {
        setAwsAccounts(result);
        return result;
      }
      setAwsAccounts([]);
      return [];
    }
  };

  // Keep the old method for backwards compatibility (permission set assignments)
  const fetchEnvAwsAccounts = async () => {
    const result = await handleApiCall(() => apiService.getAwsAccounts());
    if (result && result.success) {
      const accounts = result.data.accounts || [];
      return accounts;
    } else {
      // Handle case where API might return accounts directly
      if (result && Array.isArray(result)) {
        return result;
      }
      return [];
    }
  };
  
  // Permission Set Assignment operations
  const fetchPermissionSetAssignments = async (filters = {}) => {
    const result = await handleApiCall(() => apiService.getAllPermissionSetAssignments(filters));
    if (result && result.success) {
      const assignments = result.data.assignments || [];
      setPermissionSetAssignments(assignments);
      return assignments;
    } else {
      // Handle case where API might return assignments directly
      if (result && Array.isArray(result)) {
        setPermissionSetAssignments(result);
        return result;
      }
      setPermissionSetAssignments([]);
      return [];
    }
  };

  const getPermissionSetAssignment = async (id) => {
    const result = await handleApiCall(() => apiService.getPermissionSetAssignment(id));
    if (result && result.success) {
      return result.data;
    }
    return null;
  };

  const createPermissionSetAssignment = async (assignmentData) => {
    const accountIds = Array.isArray(assignmentData.accountId) 
      ? assignmentData.accountId 
      : [assignmentData.accountId];
    
    const successMessage = accountIds.length > 1
      ? `Permission set assigned successfully to ${accountIds.length} AWS accounts`
      : 'Permission set assigned successfully';
    
    const result = await handleApiCall(
      () => apiService.createPermissionSetAssignment(assignmentData),
      successMessage
    );
    if (result && result.success) {
      fetchPermissionSetAssignments();
      return true;
    }
    return false;
  };

  const deletePermissionSetAssignment = async (id, skipConfirm = false) => {
    if (!skipConfirm) {
      const confirmed = await confirm({
        title: 'Delete Assignment',
        message: 'Are you sure you want to delete this permission set assignment?',
        confirmText: 'Delete',
        variant: 'danger',
      });
      if (!confirmed) {
        return false;
      }
    }

    const result = await handleApiCall(
      () => apiService.deletePermissionSetAssignment(id),
      'Permission set assignment deleted successfully'
    );
    if (result && result.success) {
      fetchPermissionSetAssignments();
      return true;
    }
    return false;
  };

  // Replication state - simplified to match available API
  const [ssoConfiguration, setSsoConfiguration] = useState(null);
  const [replicationLoading, setReplicationLoading] = useState(false);
  const [userReplicationResult, setUserReplicationResult] = useState(null);
  const [accountOnboardingResult, setAccountOnboardingResult] = useState(null);
  const [fullReplicationResult, setFullReplicationResult] = useState(null);
  // Live per-resource counters published by the backend on every poll of the
  // two long-running jobs. Kept separate so a stale fetch table never shows up
  // underneath a running replication.
  const [ssoFetchProgress, setSsoFetchProgress] = useState(null);
  const [replicationProgress, setReplicationProgress] = useState(null);

  // Replication API functions - uses async polling (backend returns 202 while fetching)
  const fetchSsoConfiguration = useCallback(async () => {
    const poll = async () => {
      // Large Identity Center directories (thousands of accounts x permission
      // sets) take minutes to enumerate. Give up well after the realistic worst
      // case rather than at the 2 minutes that used to cut healthy fetches off.
      const maxAttempts = 150; // 150 * 2s = 300 seconds max
      const pollInterval = 2000;
      // A dropped poll must not abandon a job that is still running on the
      // server. Network errors are retried; only a sustained outage gives up.
      const maxConsecutiveErrors = 5;
      let consecutiveErrors = 0;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        let result;
        try {
          result = await apiService.fetchSsoConfiguration();
          consecutiveErrors = 0;
        } catch (networkError) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= maxConsecutiveErrors) {
            throw networkError;
          }
          await new Promise(r => setTimeout(r, pollInterval));
          continue;
        }

        if (result && result.success) {
          // 202: backend still fetching in background
          if (result.data && result.data.status === 'fetching') {
            if (result.data.progress) {
              setSsoFetchProgress(result.data.progress);
            }
            await new Promise(r => setTimeout(r, pollInterval));
            continue;
          }
          // 200: data ready — result.data contains the SSO configuration directly
          //       or wrapped in {status: "ready", data: ...} from the new response format
          return result;
        }
        throw new Error(result?.message || 'Failed to fetch SSO configuration');
      }
      // The backend keeps fetching after we stop polling and caches the result,
      // so a retry usually returns immediately rather than starting over.
      throw new Error('SSO configuration is taking longer than expected. It is still being fetched in the background — try again in a minute.');
    };

    setSsoFetchProgress(null);
    const result = await handleApiCall(poll, 'SSO configuration fetched successfully');
    if (result && result.success) {
      // Handle both direct config response (200 with config) and wrapped response
      const configData = result.data;
      setSsoConfiguration({
        users: configData.users || [],
        groups: configData.groups || [],
        permission_sets: configData.permission_sets || [],
        user_assignments: configData.user_assignments || [],
        accounts: configData.accounts || [],
      });
      // The fetched configuration replaces the progress table.
      setSsoFetchProgress(null);
      return configData;
    }
    return null;
  }, []);

  // Poll replication status until completed or error
  const pollReplicationStatus = useCallback(async (successMessage) => {
    // Replicating a large directory writes every user and group to Keycloak and
    // resolves each user's group memberships against AWS, so it runs for tens of
    // minutes at tenant scale. The old 10-minute ceiling gave up long before the
    // job did, leaving a run that was still progressing looking like a failure.
    const maxAttempts = 1800; // 1800 * 2s = 3600 seconds (60 min) max
    const pollInterval = 2000;
    // Drop the previous run's counters so the table never mixes two jobs.
    // The final snapshot is kept after completion so the totals stay readable.
    setReplicationProgress(null);
    // Replication runs for many minutes; a single dropped poll must not stop us
    // tracking a job that is still running server-side.
    const maxConsecutiveErrors = 5;
    let consecutiveErrors = 0;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      let result;
      try {
        result = await apiService.getReplicationStatus();
        consecutiveErrors = 0;
      } catch (networkError) {
        consecutiveErrors += 1;
        if (consecutiveErrors >= maxConsecutiveErrors) {
          throw networkError;
        }
        await new Promise(r => setTimeout(r, pollInterval));
        continue;
      }

      if (result && result.success) {
        const status = result.data?.status;
        if (result.data?.progress) {
          setReplicationProgress(result.data.progress);
        }
        if (status === 'running') {
          await new Promise(r => setTimeout(r, pollInterval));
          continue;
        }
        if (status === 'completed') {
          return result;
        }
        if (status === 'error') {
          throw new Error(result.data?.error || 'Replication failed');
        }
        // "idle" or unexpected — return as-is
        return result;
      }
      throw new Error(result?.message || 'Failed to get replication status');
    }
    throw new Error('Replication timed out. Please check the status manually.');
  }, []);

  // Async user and group replication function
  const replicateUsersToKeycloak = useCallback(async (dryRun = false) => {
    // Trigger async replication
    const triggerResult = await handleApiCall(
      () => apiService.replicateUsersToKeycloak(dryRun),
      null
    );
    if (!triggerResult || !triggerResult.success) return null;

    // If backend returned completed result immediately (cached), use it
    if (triggerResult.data?.status === 'completed' && triggerResult.data?.data) {
      const data = triggerResult.data.data;
      setUserReplicationResult(data);
      setSuccess(dryRun ? 'Dry run completed successfully' : 'Users, groups, and assignments replicated to Keycloak successfully');
      if (data.successful_assignment_sync > 0) fetchPermissionSetAssignments();
      return data;
    }

    // Poll for completion
    const result = await handleApiCall(
      () => pollReplicationStatus(),
      dryRun ? 'Dry run completed successfully' : 'Users, groups, and assignments replicated to Keycloak successfully - all users have offline access enabled'
    );
    if (result && result.success && result.data?.data) {
      const data = result.data.data;
      setUserReplicationResult(data);
      if (data.successful_assignment_sync > 0) fetchPermissionSetAssignments();
      return data;
    }
    return null;
  }, []);

  // Async account onboarding function
  const onboardAWSAccounts = useCallback(async (dryRun = false) => {
    const triggerResult = await handleApiCall(
      () => apiService.onboardAWSAccounts(dryRun),
      null
    );
    if (!triggerResult || !triggerResult.success) return null;

    if (triggerResult.data?.status === 'completed' && triggerResult.data?.data) {
      const data = triggerResult.data.data;
      setAccountOnboardingResult(data);
      setSuccess(dryRun ? 'Account onboarding dry run completed successfully' : 'AWS accounts onboarded successfully');
      return data;
    }

    const result = await handleApiCall(
      () => pollReplicationStatus(),
      dryRun ? 'Account onboarding dry run completed successfully' : 'AWS accounts onboarded successfully'
    );
    if (result && result.success && result.data?.data) {
      const data = result.data.data;
      setAccountOnboardingResult(data);
      return data;
    }
    return null;
  }, []);

  // Async full replication function
  const replicateFullConfiguration = useCallback(async (dryRun = false) => {
    const triggerResult = await handleApiCall(
      () => apiService.replicateFullConfiguration(dryRun),
      null
    );
    if (!triggerResult || !triggerResult.success) return null;

    if (triggerResult.data?.status === 'completed' && triggerResult.data?.data) {
      const data = triggerResult.data.data;
      setFullReplicationResult(data);
      if (data.user_replication_result) setUserReplicationResult(data.user_replication_result);
      if (data.account_onboarding_result) setAccountOnboardingResult(data.account_onboarding_result);
      setSuccess(dryRun ? 'Full replication dry run completed successfully' : 'Full SSO configuration replicated successfully');
      if (!dryRun) {
        if (data.user_replication_result?.successful_assignment_sync > 0) fetchPermissionSetAssignments();
        if (data.account_onboarding_result?.successful_onboarding > 0) fetchAwsAccounts();
      }
      return data;
    }

    const result = await handleApiCall(
      () => pollReplicationStatus(),
      dryRun ? 'Full replication dry run completed successfully' : 'Full SSO configuration replicated successfully'
    );
    if (result && result.success && result.data?.data) {
      const data = result.data.data;
      setFullReplicationResult(data);
      if (data.user_replication_result) setUserReplicationResult(data.user_replication_result);
      if (data.account_onboarding_result) setAccountOnboardingResult(data.account_onboarding_result);
      if (!dryRun) {
        if (data.user_replication_result?.successful_assignment_sync > 0) fetchPermissionSetAssignments();
        if (data.account_onboarding_result?.successful_onboarding > 0) fetchAwsAccounts();
      }
      return data;
    }
    return null;
  }, []);

  // AWS Account management functions
  const createAWSAccount = useCallback(async (accountData) => {
    const result = await handleApiCall(
      () => apiService.createAWSAccount(accountData),
      'AWS account created successfully'
    );
    return result && result.success;
  }, []);

  const getAWSAccount = useCallback(async (accountId) => {
    const result = await handleApiCall(() => apiService.getAWSAccount(accountId));
    if (result && result.success) {
      return result.data;
    }
    return null;
  }, []);

  const getAllAWSAccounts = useCallback(async () => {
    const result = await handleApiCall(() => apiService.getAllAWSAccounts());
    if (result && result.success) {
      return result.data.accounts || [];
    }
    return [];
  }, []);

  const updateAWSAccount = useCallback(async (accountId, accountData) => {
    const result = await handleApiCall(
      () => apiService.updateAWSAccount(accountId, accountData),
      'AWS account updated successfully'
    );
    return result && result.success;
  }, []);

  const deleteAWSAccount = useCallback(async (accountId) => {
    const result = await handleApiCall(
      () => apiService.deleteAWSAccount(accountId),
      'AWS account deleted successfully'
    );
    return result && result.success;
  }, []);

  // Keycloak initialization functions
  const initializeKeycloak = useCallback(async () => {
    const result = await handleApiCall(() => apiService.initializeKeycloak());
    if (result && result.success) {
      return result.data;
    }
    return null;
  }, []);

  const getKeycloakStatus = useCallback(async () => {
    const result = await handleApiCall(() => apiService.getKeycloakStatus());
    if (result && result.success) {
      return result.data;
    }
    return null;
  }, []);

  const validateKeycloakConfiguration = useCallback(async () => {
    const result = await handleApiCall(() => apiService.validateKeycloakConfiguration());
    if (result && result.success) {
      return result.data;
    }
    return null;
  }, []);

  // Admin Management functions
  const listAdmins = useCallback(async () => {
    const result = await handleApiCall(() => apiService.listAdmins());
    if (result && result.success) {
      // Handle both response formats (app admins vs customer admins)
      if (result.data.admins) {
        return result.data;
      } else if (result.data && Array.isArray(result.data)) {
        return { admins: result.data, total: result.data.length };
      } else {
        // Handle case where data might be directly the admin array
        const admins = Array.isArray(result.data) ? result.data : [];
        return { admins, total: admins.length };
      }
    }
    return { admins: [], total: 0 };
  }, []);

  const promoteUserToAdmin = useCallback(async (username) => {
    const result = await handleApiCall(
      () => apiService.promoteUserToAdmin(username),
      `User ${username} promoted to admin successfully`
    );
    return result && result.success;
  }, []);

  const demoteAdmin = useCallback(async (adminId) => {
    const result = await handleApiCall(
      () => apiService.demoteAdmin(adminId),
      'Admin user demoted successfully'
    );
    return result && result.success;
  }, []);

  const getCurrentAdmin = useCallback(async () => {
    const result = await handleApiCall(() => apiService.getCurrentAdmin());
    if (result && result.success) {
      return result.data;
    }
    return null;
  }, []);

  // Add customer management state
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Customer management methods
  const createCustomer = async (customerData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.createCustomer(customerData);
      if (response.success) {
        setSuccess('Customer created successfully');
        await fetchCustomers(); // Refresh list
        return response.data;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.listCustomers();
      if (response.success) {
        setCustomers(response.data.customers || []);
        return response.data.customers;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getCustomer = async (customerId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getCustomer(customerId);
      if (response.success) {
        setSelectedCustomer(response.data);
        return response.data;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCustomer = async (customerId, customerData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.updateCustomer(customerId, customerData);
      if (response.success) {
        setSuccess('Customer updated successfully');
        await fetchCustomers(); // Refresh list
        return response.data;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const initializeCustomerAdmin = async (customerId, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.initializeCustomerAdmin(customerId, password);
      if (response.success) {
        setSuccess('Customer admin initialized successfully');
        return response.data;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const customerLogin = async (customerId, username, password, totpCode = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.customerLogin(customerId, username, password, totpCode);
      if (response.success) {
        setSuccess('Customer login successful');
        // Return the actual data, not the wrapper
        return response;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Management Account operations
  const getManagementAccount = useCallback(async () => {
    const result = await handleApiCall(() => apiService.getManagementAccount());
    if (result && result.success) {
      return result.data;
    }
    return null;
  }, []);

  const initializeManagementAccount = useCallback(async (accountData) => {
    const result = await handleApiCall(
      () => apiService.initializeManagementAccount(accountData),
      'Management account configured successfully'
    );
    return result && result.success;
  }, []);

  const deleteManagementAccount = useCallback(async () => {
    const result = await handleApiCall(
      () => apiService.deleteManagementAccount(),
      'Management account configuration deleted successfully'
    );
    return result && result.success;
  }, []);

  // Google Identity Provider state - now an array for multiple IdPs
  const [googleIdPs, setGoogleIdPs] = useState([]);
  const [googleIdPsLimit, setGoogleIdPsLimit] = useState(10);
  // Microsoft Identity Provider state - now an array for multiple IdPs
  const [microsoftIdPs, setMicrosoftIdPs] = useState([]);
  const [microsoftIdPsLimit, setMicrosoftIdPsLimit] = useState(10);
  const [idPStatus, setIdPStatus] = useState(null);

  // Google Identity Provider methods - updated for multi-IdP support

  // List all Google IdPs
  const listGoogleIdPs = async () => {
    const result = await handleApiCall(() => apiService.listGoogleIdPs());
    if (result && result.success) {
      setGoogleIdPs(result.data.identityProviders || []);
      setGoogleIdPsLimit(result.data.limit || 10);
      return result.data.identityProviders || [];
    } else {
      setGoogleIdPs([]);
      return [];
    }
  };

  // Create a new Google IdP
  const createGoogleIdP = async (googleIdPData) => {
    const result = await handleApiCall(
      () => apiService.createGoogleIdP(googleIdPData),
      'Google Identity Provider created successfully'
    );
    if (result && result.success) {
      await listGoogleIdPs();
      await fetchIdPStatus();
      return result.data.identityProvider;
    }
    return null;
  };

  // Get a specific Google IdP by alias
  const fetchGoogleIdPByAlias = async (alias) => {
    const result = await handleApiCall(() => apiService.getGoogleIdPByAlias(alias));
    if (result && result.success) {
      return result.data.identityProvider;
    }
    return null;
  };

  // Update a specific Google IdP by alias
  const updateGoogleIdP = async (alias, googleIdPData) => {
    const result = await handleApiCall(
      () => apiService.updateGoogleIdP(alias, googleIdPData),
      'Google Identity Provider updated successfully'
    );
    if (result && result.success) {
      await listGoogleIdPs();
      await fetchIdPStatus();
      return true;
    }
    return false;
  };

  // Delete a specific Google IdP by alias
  const deleteGoogleIdP = async (alias, skipConfirm = false) => {
    if (!skipConfirm) {
      const confirmed = await confirm({
        title: 'Delete Identity Provider',
        message: 'Are you sure you want to delete this Google Identity Provider?',
        confirmText: 'Delete',
        variant: 'danger',
      });
      if (!confirmed) {
        return false;
      }
    }

    const result = await handleApiCall(
      () => apiService.deleteGoogleIdP(alias),
      'Google Identity Provider deleted successfully'
    );
    if (result && result.success) {
      await listGoogleIdPs();
      await fetchIdPStatus();
      return true;
    }
    return false;
  };

  const validateGoogleIdPConfig = async (validationData) => {
    const result = await handleApiCall(
      () => apiService.validateGoogleIdPConfig(validationData),
      null // Don't show success message for validation
    );
    if (result && result.success) {
      return result.data.validation;
    }
    return null;
  };

  // Microsoft Identity Provider methods - updated for multi-IdP support

  // List all Microsoft IdPs
  const listMicrosoftIdPs = async () => {
    const result = await handleApiCall(() => apiService.listMicrosoftIdPs());
    if (result && result.success) {
      setMicrosoftIdPs(result.data.identityProviders || []);
      setMicrosoftIdPsLimit(result.data.limit || 10);
      return result.data.identityProviders || [];
    } else {
      setMicrosoftIdPs([]);
      return [];
    }
  };

  // Create a new Microsoft IdP
  const createMicrosoftIdP = async (microsoftIdPData) => {
    const result = await handleApiCall(
      () => apiService.createMicrosoftIdP(microsoftIdPData),
      'Microsoft Identity Provider created successfully'
    );
    if (result && result.success) {
      await listMicrosoftIdPs();
      await fetchIdPStatus();
      return result.data.identityProvider;
    }
    return null;
  };

  // Get a specific Microsoft IdP by alias
  const fetchMicrosoftIdPByAlias = async (alias) => {
    const result = await handleApiCall(() => apiService.getMicrosoftIdPByAlias(alias));
    if (result && result.success) {
      return result.data.identityProvider;
    }
    return null;
  };

  // Update a specific Microsoft IdP by alias
  const updateMicrosoftIdP = async (alias, microsoftIdPData) => {
    const result = await handleApiCall(
      () => apiService.updateMicrosoftIdP(alias, microsoftIdPData),
      'Microsoft Identity Provider updated successfully'
    );
    if (result && result.success) {
      await listMicrosoftIdPs();
      await fetchIdPStatus();
      return true;
    }
    return false;
  };

  // Delete a specific Microsoft IdP by alias
  const deleteMicrosoftIdP = async (alias, skipConfirm = false) => {
    if (!skipConfirm) {
      const confirmed = await confirm({
        title: 'Delete Identity Provider',
        message: 'Are you sure you want to delete this Microsoft Identity Provider?',
        confirmText: 'Delete',
        variant: 'danger',
      });
      if (!confirmed) {
        return false;
      }
    }

    const result = await handleApiCall(
      () => apiService.deleteMicrosoftIdP(alias),
      'Microsoft Identity Provider deleted successfully'
    );
    if (result && result.success) {
      await listMicrosoftIdPs();
      await fetchIdPStatus();
      return true;
    }
    return false;
  };

  const validateMicrosoftIdPConfig = async (validationData) => {
    const result = await handleApiCall(
      () => apiService.validateMicrosoftIdPConfig(validationData),
      null // Don't show success message for validation
    );
    if (result && result.success) {
      return result.data.validation;
    }
    return null;
  };

  // Custom Identity Provider state - now an array for multiple IdPs
  const [customIdPs, setCustomIdPs] = useState([]);
  const [customIdPsLimit, setCustomIdPsLimit] = useState(10);

  // SAML 2.0 Identity Provider state - same array shape as the other three types
  const [samlIdPs, setSamlIdPs] = useState([]);
  const [samlIdPsLimit, setSamlIdPsLimit] = useState(10);

  // Realm login settings state
  const [realmLoginSettings, setRealmLoginSettings] = useState({ idpOnlyLogin: false, loginTheme: 'ck-prism' });

  // Custom Identity Provider methods - updated for multi-IdP support

  // List all Custom IdPs
  const listCustomIdPs = async () => {
    const result = await handleApiCall(() => apiService.listCustomIdPs());
    if (result && result.success) {
      setCustomIdPs(result.data.identityProviders || []);
      setCustomIdPsLimit(result.data.limit || 10);
      return result.data.identityProviders || [];
    } else {
      setCustomIdPs([]);
      return [];
    }
  };

  // Create a new Custom IdP
  const createCustomIdP = async (customIdPData) => {
    const result = await handleApiCall(
      () => apiService.createCustomIdP(customIdPData),
      'Custom Identity Provider created successfully'
    );
    if (result && result.success) {
      await listCustomIdPs();
      await fetchIdPStatus();
      return result.data.identityProvider;
    }
    return null;
  };

  // Get a specific Custom IdP by alias
  const fetchCustomIdPByAlias = async (alias) => {
    const result = await handleApiCall(() => apiService.getCustomIdPByAlias(alias));
    if (result && result.success) {
      return result.data.identityProvider;
    }
    return null;
  };

  // Update a specific Custom IdP by alias
  const updateCustomIdP = async (alias, customIdPData) => {
    const result = await handleApiCall(
      () => apiService.updateCustomIdP(alias, customIdPData),
      'Custom Identity Provider updated successfully'
    );
    if (result && result.success) {
      await listCustomIdPs();
      await fetchIdPStatus();
      return true;
    }
    return false;
  };

  // Delete a specific Custom IdP by alias
  const deleteCustomIdP = async (alias, skipConfirm = false) => {
    if (!skipConfirm) {
      const confirmed = await confirm({
        title: 'Delete Identity Provider',
        message: 'Are you sure you want to delete this Custom Identity Provider?',
        confirmText: 'Delete',
        variant: 'danger',
      });
      if (!confirmed) {
        return false;
      }
    }

    const result = await handleApiCall(
      () => apiService.deleteCustomIdP(alias),
      'Custom Identity Provider deleted successfully'
    );
    if (result && result.success) {
      await listCustomIdPs();
      await fetchIdPStatus();
      return true;
    }
    return false;
  };

  const validateCustomIdPConfig = async (configData) => {
    const result = await handleApiCall(
      () => apiService.validateCustomIdPConfig(configData),
      null // Don't show success message for validation
    );
    if (result && result.success) {
      return result.data.validation;
    }
    return null;
  };

  // SAML 2.0 Identity Provider methods
  //
  // Defined above fetchIdPStatus because create/update/delete call it; JS
  // function hoisting is not in play for the const arrow declarations, but the
  // calls are inside async bodies that only run after the whole hook body has
  // evaluated, so the ordering is a readability choice, matching the other three
  // provider blocks.

  // List all SAML IdPs
  const listSamlIdPs = async () => {
    const result = await handleApiCall(() => apiService.listSamlIdPs());
    if (result && result.success) {
      setSamlIdPs(result.data.identityProviders || []);
      setSamlIdPsLimit(result.data.limit || 10);
      return result.data.identityProviders || [];
    } else {
      setSamlIdPs([]);
      return [];
    }
  };

  // Create a new SAML IdP.
  //
  // Returns the full provider object, not a boolean: the alias is minted
  // server-side and GenerateUniqueAlias appends -1/-2 on collision, so the
  // dialog's preview alias -- and every SP URL derived from it -- may be wrong.
  // SamlIdPList hands this object straight to SamlConnectionDetails, which is the
  // authoritative source for the values the admin pastes into their IdP.
  const createSamlIdP = async (samlIdPData) => {
    const result = await handleApiCall(
      () => apiService.createSamlIdP(samlIdPData),
      'SAML Identity Provider created successfully'
    );
    if (result && result.success) {
      await listSamlIdPs();
      await fetchIdPStatus();
      return result.data.identityProvider;
    }
    return null;
  };

  // Get a specific SAML IdP by alias
  const fetchSamlIdPByAlias = async (alias) => {
    const result = await handleApiCall(() => apiService.getSamlIdPByAlias(alias));
    if (result && result.success) {
      return result.data.identityProvider;
    }
    return null;
  };

  // Update a specific SAML IdP by alias
  const updateSamlIdP = async (alias, samlIdPData) => {
    const result = await handleApiCall(
      () => apiService.updateSamlIdP(alias, samlIdPData),
      'SAML Identity Provider updated successfully'
    );
    if (result && result.success) {
      await listSamlIdPs();
      await fetchIdPStatus();
      return true;
    }
    return false;
  };

  // Delete a specific SAML IdP by alias
  const deleteSamlIdP = async (alias, skipConfirm = false) => {
    if (!skipConfirm) {
      const confirmed = await confirm({
        title: 'Delete Identity Provider',
        message: 'Are you sure you want to delete this SAML Identity Provider?',
        confirmText: 'Delete',
        variant: 'danger',
      });
      if (!confirmed) {
        return false;
      }
    }

    const result = await handleApiCall(
      () => apiService.deleteSamlIdP(alias),
      'SAML Identity Provider deleted successfully'
    );
    if (result && result.success) {
      await listSamlIdPs();
      await fetchIdPStatus();
      return true;
    }
    return false;
  };

  // Import a SAML metadata document (URL or raw XML).
  //
  // Deliberately bypasses handleApiCall, and deliberately touches neither
  // setLoading nor setError:
  //   - handleApiCall swallows the thrown error into `null`, and the backend's
  //     import diagnostics ("document contains no IDPSSODescriptor", the SSRF
  //     rejections) are the entire value of this endpoint. The caller needs the
  //     message, so it needs the throw.
  //   - setLoading drives the global `loading` prop, which relabels the dialog's
  //     Create button to "Saving...". The dialog tracks its own `importing` flag.
  //   - setError feeds a surface that auto-clears after 3 seconds; the import
  //     failure has to persist as an inline Alert until the admin acts on it.
  const importSamlMetadata = async ({ metadataUrl, metadataXml } = {}) => {
    const response = await apiService.importSamlMetadata({ metadataUrl, metadataXml });
    if (response && response.success) {
      return response.data;
    }
    throw new Error((response && response.message) || 'Failed to import SAML metadata');
  };

  // Discover OIDC endpoints from a discovery URL
  const discoverOIDCEndpoints = async (discoveryUrl) => {
    const result = await handleApiCall(
      () => apiService.discoverOIDCEndpoints(discoveryUrl),
      null // Don't show success message - component handles it
    );
    if (result && result.success) {
      return result.data.discovery;
    }
    return null;
  };

  // Update fetchIdPStatus to include Microsoft IdP
  const fetchIdPStatus = async () => {
    const result = await handleApiCall(() => apiService.getIdPStatus());
    if (result && result.success) {
      setIdPStatus(result.data.status || result.data);
      return result.data.status || result.data;
    } else {
      setIdPStatus(null);
      return null;
    }
  };

  // Realm Login Settings methods
  const fetchRealmLoginSettings = async () => {
    const result = await handleApiCall(() => apiService.getRealmLoginSettings());
    if (result && result.success) {
      setRealmLoginSettings(result.data || { idpOnlyLogin: false, loginTheme: 'ck-prism' });
      return result.data;
    } else {
      setRealmLoginSettings({ idpOnlyLogin: false, loginTheme: 'ck-prism' });
      return null;
    }
  };

  const updateRealmLoginSettings = async (idpOnlyLogin) => {
    const result = await handleApiCall(
      () => apiService.updateRealmLoginSettings(idpOnlyLogin),
      idpOnlyLogin ? 'IdP-only login enabled successfully' : 'Standard login restored successfully'
    );
    if (result && result.success) {
      setRealmLoginSettings(result.data || { idpOnlyLogin, loginTheme: idpOnlyLogin ? 'ck-prism-idp-only' : 'ck-prism' });
      return true;
    }
    return false;
  };

  // Add Custom SAML Application state
  const [customApplications, setCustomApplications] = useState([]);

  // Custom SAML Application methods
  const createCustomApplication = async (customerId, applicationData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.createCustomApplication(customerId, applicationData);
      if (response.success) {
        setSuccess('Custom application created successfully');
        await fetchCustomApplications(customerId);
        return response.data;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomApplications = async (customerId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.listCustomApplications(customerId);
      if (response.success) {
        setCustomApplications(response.data.applications || []);
        return response.data.applications;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getCustomApplication = async (customerId, applicationId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getCustomApplication(customerId, applicationId);
      if (response.success) {
        return response.data;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCustomApplication = async (customerId, applicationId, applicationData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.updateCustomApplication(customerId, applicationId, applicationData);
      if (response.success) {
        setSuccess('Custom application updated successfully');
        await fetchCustomApplications(customerId);
        return response.data;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /*
   * Deleting a custom application. Throws on every failure; the caller owns the confirmation
   * and owns reporting.
   *
   * There is deliberately no confirm() here. This used to open a second, generic prompt of its
   * own on top of the one its only caller already shows — and answered a dismissal with a bare
   * `return false`, so a delete could be cancelled with nothing set, nothing thrown and nothing
   * shown. The caller's prompt names the application, which this one could not.
   */
  const deleteCustomApplication = async (customerId, applicationId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.deleteCustomApplication(customerId, applicationId);
      if (!response || !response.success) {
        /*
         * api.js only throws on a non-2xx, so a 200 whose body reports failure arrives here as
         * an ordinary value. It used to fall out of the bottom of this function as `undefined`
         * with no error state and no throw, and the caller had no way to tell it from a
         * success — the delete simply appeared to do nothing.
         */
        throw new Error(response?.message || 'Failed to delete the application');
      }
      setSuccess('Custom application deleted successfully');
      await fetchCustomApplications(customerId);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const uploadCustomApplicationIcon = async (customerId, applicationId, file) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.uploadCustomApplicationIcon(customerId, applicationId, file);
      if (response.success) {
        setSuccess('Application icon uploaded successfully');
        return response.data;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteCustomApplicationIcon = async (customerId, applicationId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.deleteCustomApplicationIcon(customerId, applicationId);
      if (response.success) {
        setSuccess('Application icon deleted successfully');
        return response.data;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getCurrentCustomerInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getCurrentCustomer();
      if (response.success) {
        return response.data.customer;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // New customer deletion method
  const deleteCustomer = async (customerId) => {
    const confirmed = await confirm({
      title: 'Delete Customer',
      message: 'Are you sure you want to delete this customer? This action is irreversible and will permanently delete all customer data including users, groups, permission sets, and AWS configurations.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) {
      return false;
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.deleteCustomer(customerId);
      if (response.success) {
        // Handle different success scenarios
        if (response.data && response.data.keycloakRealm && !response.data.keycloakRealm.deleted) {
          setSuccess('Customer deleted from database but Keycloak realm deletion failed - see response for details');
        } else {
          setSuccess('Customer deleted successfully');
        }
        await fetchCustomers(); // Refresh customer list
        return response;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getCustomerResourceCounts = async (customerId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getCustomerResourceCounts(customerId);
      if (response.success) {
        return response.data;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Reset customer admin credentials (password and/or MFA)
  const resetCustomerAdmin = async (customerId, { newPassword, resetMFA }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.resetCustomerAdmin(customerId, { newPassword, resetMFA });
      if (response.success) {
        return response;
      }
      throw new Error(response.message || 'Failed to reset admin credentials');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // AWS Account deboard functionality
  const deboardAWSAccount = async (accountId) => {
    const confirmed = await confirm({
      title: 'Deboard AWS Account',
      message: `Are you sure you want to deboard AWS account ${accountId}? This will permanently remove it from the system and cannot be undone.`,
      confirmText: 'Deboard',
      variant: 'danger',
    });
    if (!confirmed) {
      return false;
    }
    
    try {
      // First check if there are any active assignments for this account
      const assignmentsResponse = await apiService.checkAWSAccountAssignments(accountId);
      
      if (assignmentsResponse.success && assignmentsResponse.data && assignmentsResponse.data.assignments) {
        const assignments = assignmentsResponse.data.assignments;
        if (assignments.length > 0) {
          const assignmentDetails = assignments.map(assignment => 
            `• ${assignment.principalName || assignment.username || assignment.groupName} → ${assignment.permissionSetName}`
          ).join('\n');
          
          setError(`Cannot deboard AWS account ${accountId} because it has ${assignments.length} active assignment(s):\n\n${assignmentDetails}\n\nPlease remove all assignments before deboardng this account.`);
          return false;
        }
      }
      
      // No assignments found, proceed with deboard
      const result = await handleApiCall(
        () => apiService.deboardAWSAccount(accountId),
        'AWS account deboarded successfully'
      );
      
      if (result && result.success) {
        // Refresh the accounts list after successful deboard
        fetchAwsAccounts();
        return true;
      }
      return false;
    } catch (error) {
      // Handle specific error cases from the backend
      if (error.message && error.message.includes('active assignment')) {
        setError(`Cannot deboard AWS account ${accountId}: ${error.message}`);
      } else {
        setError(`Failed to deboard AWS account ${accountId}: ${error.message}`);
      }
      return false;
    }
  };

  // User password management
  const changeUserPassword = async (username, newPassword, temporary = false) => {
    const result = await handleApiCall(
      () => apiService.changeUserPassword(username, newPassword, temporary),
      'Password changed successfully'
    );
    return result;
  };

  return {
    users,
    usersTotal,
    groups,
    groupsTotal,
    groupMembers,
    permissionSets,
    awsManagedPolicies,
    loading,
    error,
    success,
    fetchAllUsers,
    fetchAllGroups,
    fetchGroupMembers,
    createUser,
    createGroup,
    deleteGroup,
    getUser,
    getGroup,
    updateUser,
    deleteUser,
    assignUsersToGroup,
    removeUsersFromGroup,
    // Permission set methods
    fetchAllPermissionSets,
    getPermissionSet,
    createPermissionSet,
    updatePermissionSet,
    deletePermissionSet,
    // AWS Managed Policy methods
    fetchAwsManagedPolicies,
    getAwsManagedPolicy,
    // AWS Account methods
    awsAccounts, // Database accounts (objects)
    fetchAwsAccounts, // Fetches database accounts
    fetchEnvAwsAccounts, // Fetches environment accounts (strings)
    // Permission set assignment methods
    permissionSetAssignments,
    fetchPermissionSetAssignments,
    getPermissionSetAssignment,
    createPermissionSetAssignment,
    deletePermissionSetAssignment,
    // Replication data
    ssoConfiguration,
    userReplicationResult,
    accountOnboardingResult,
    fullReplicationResult,
    replicationConfigs: [], // Always empty since endpoint doesn't exist
    replicationJobs: [], // Always empty since endpoint doesn't exist
    
    // Updated replication methods
    fetchSsoConfiguration,
    replicateUsersToKeycloak,
    onboardAWSAccounts,
    replicateFullConfiguration,
    ssoFetchProgress,
    replicationProgress,

    // AWS Account management methods
    createAWSAccount,
    getAWSAccount,
    getAllAWSAccounts,
    updateAWSAccount,
    deleteAWSAccount,
    
    // Keycloak initialization methods
    initializeKeycloak,
    getKeycloakStatus,
    validateKeycloakConfiguration,
    
    // Admin management methods
    listAdmins,
    promoteUserToAdmin,
    demoteAdmin,
    getCurrentAdmin,
    
    // Management Account methods
    getManagementAccount,
    initializeManagementAccount,
    deleteManagementAccount,
    
    // Customer management
    customers,
    selectedCustomer,
    createCustomer,
    fetchCustomers,
    getCustomer,
    updateCustomer,
    deleteCustomer,
    getCustomerResourceCounts,
    resetCustomerAdmin,

    // Google Identity Provider methods
    googleIdPs,
    googleIdPsLimit,
    idPStatus,
    createGoogleIdP,
    listGoogleIdPs,
    fetchGoogleIdPByAlias,
    updateGoogleIdP,
    deleteGoogleIdP,
    fetchIdPStatus,
    validateGoogleIdPConfig,
    
    // Microsoft Identity Provider methods
    microsoftIdPs,
    microsoftIdPsLimit,
    createMicrosoftIdP,
    listMicrosoftIdPs,
    fetchMicrosoftIdPByAlias,
    updateMicrosoftIdP,
    deleteMicrosoftIdP,
    validateMicrosoftIdPConfig,

    // Custom IdP methods
    customIdPs,
    customIdPsLimit,
    createCustomIdP,
    listCustomIdPs,
    fetchCustomIdPByAlias,
    updateCustomIdP,
    deleteCustomIdP,
    validateCustomIdPConfig,
    discoverOIDCEndpoints,

    // SAML 2.0 IdP methods
    samlIdPs,
    samlIdPsLimit,
    createSamlIdP,
    listSamlIdPs,
    fetchSamlIdPByAlias,
    updateSamlIdP,
    deleteSamlIdP,
    importSamlMetadata,

    // Realm Login Settings
    realmLoginSettings,
    fetchRealmLoginSettings,
    updateRealmLoginSettings,

    // Custom SAML Application methods
    customApplications,
    createCustomApplication,
    fetchCustomApplications,
    getCustomApplication,
    updateCustomApplication,
    deleteCustomApplication,
    uploadCustomApplicationIcon,
    deleteCustomApplicationIcon,
    
    getCurrentCustomerInfo,
    
    // AWS Account deboard methods
    deboardAWSAccount,
    changeUserPassword,
  };
};